import type { ApolloQueryResult, QueryResult } from "@apollo/client";
import { useQuery } from "@apollo/client";
import { useAbility } from "@casl/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { authzPermissionsContext } from "context/authz/config";
import type { Exact, GetStakeholdersQuery, Scalars } from "gql/graphql";
import { useAudit } from "hooks/use-audit";
import { GET_STAKEHOLDERS } from "pages/group/queries";
import type { IStakeholderDataSet } from "pages/organization/members/types";
import { Logger } from "utils/logger";
import { msgError } from "utils/notifications";

type TQueryResult = QueryResult<
  GetStakeholdersQuery,
  Exact<{ groupName: Scalars["String"]["input"] }>
>;

interface IUseStakeholdersResponse
  extends Omit<TQueryResult, "data" | "refetch"> {
  data: IStakeholderDataSet[];
  refetch: () => Promise<ApolloQueryResult<GetStakeholdersQuery>>;
}

const useStakeholders = (
  groupName: string,
  permissionSkip?: boolean,
): IUseStakeholdersResponse => {
  const { t } = useTranslation();
  const { addAuditEvent } = useAudit();
  const permissions = useAbility(authzPermissionsContext);

  const { data, loading, refetch, ...rest } = useQuery(GET_STAKEHOLDERS, {
    onCompleted: (): void => {
      addAuditEvent("Group.Stakeholders", groupName);
    },
    onError: (error): void => {
      msgError(t("groupAlerts.errorTextsad"));
      Logger.warning("An error occurred loading group members", error);
    },
    skip:
      (permissionSkip ?? false) &&
      permissions.cannot(
        "integrates_api_resolvers_query_stakeholder__resolve_for_group",
      ),
    variables: { groupName },
  });

  const stakeholdersList = useMemo(
    (): IStakeholderDataSet[] =>
      (data?.group.stakeholders ?? []) as unknown as IStakeholderDataSet[],
    [data?.group.stakeholders],
  );

  return {
    data: stakeholdersList,
    loading,
    refetch: async (): Promise<ApolloQueryResult<GetStakeholdersQuery>> =>
      refetch({ groupName }),
    ...rest,
  };
};

export { useStakeholders };
